import {  Search, Mail, Phone, Globe } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getBusinessListingsData } from '../../businessListingsData';


export default function BusinessListings() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [businessData, setBusinessData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getBusinessListingsData()
      .then((data) => {
        setBusinessData(data || {});
      })
      .catch((err) => {
        console.error('Failed to load business listings:', err);
        setBusinessData({});
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const categories = Object.keys(businessData);

  const filteredBusinesses = () => {
    const result = {};
    const search = searchTerm.toLowerCase().trim();

    Object.entries(businessData).forEach(([category, businesses]) => {
      if (selectedCategory !== 'all' && category !== selectedCategory) return;

      const filtered = businesses.filter((business) => {
        const searchableText = [
          business.name,
          business.owner,
          business.phone,
          business.email,
          business.website,
          business.description,
          category,
        ]
          .map((value) => String(value || ''))
          .join(' ')
          .toLowerCase();

        return searchableText.includes(search);
      });

      if (filtered.length > 0) {
        result[category] = filtered;
      }
    });

    return result;
  };

  const displayBusinesses = filteredBusinesses();
  const totalBusinesses = Object.values(displayBusinesses).flat().length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="text-white py-12" style={{ background: 'linear-gradient(to right, #001f3f, #003366)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Business Directory
          </h1>
          <p className="text-lg text-white/90 max-w-3xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Supporting our community's entrepreneurial spirit
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Search + Filter */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search businesses, owners, or services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <option value="all">All Categories ({categories.length})</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {!isLoading && (
            <p className="text-sm text-gray-600 mt-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Showing {totalBusinesses} business{totalBusinesses !== 1 ? 'es' : ''}
            </p>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-600 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Loading business listings...
            </p>
          </div>
        )}

        {/* Listings */}
        {!isLoading && Object.entries(displayBusinesses).map(([category, businesses]) => (
          <div key={category} className="mb-12">
            <h2
              className="text-3xl font-bold mb-6 pb-2 border-b-2 border-yellow-500"
              style={{ fontFamily: 'Playfair Display, serif', color: '#001f3f' }}
            >
              {category}
              <span
                className="text-lg font-normal text-gray-600 ml-3"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                ({businesses.length})
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {businesses.map((business, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-yellow-500"
                >
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Playfair Display, serif', color: '#001f3f' }}
                  >
                    {business.name}
                  </h3>

                  {business.owner && (
                    <p
                      className="text-sm text-gray-600 mb-3"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {business.owner}
                    </p>
                  )}

                  <p
                    className="text-gray-700 text-sm mb-4 leading-relaxed"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {business.description}
                  </p>

                  <div className="space-y-2">
                    {business.phone && (
                      <a
                        href={`tel:${business.phone.replace(/\D/g, '')}`}
                        className="flex items-center text-sm text-gray-600 hover:text-yellow-600 transition-colors"
                      >
                        <Phone size={16} className="mr-2" />
                        {business.phone}
                      </a>
                    )}

                    {business.email && (
                      <a
                        href={`mailto:${business.email}`}
                        className="flex items-center text-sm text-gray-600 hover:text-yellow-600 transition-colors"
                      >
                        <Mail size={16} className="mr-2" />
                        {business.email}
                      </a>
                    )}

                    {business.website && (
                      <a
                        href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-sm text-gray-600 hover:text-yellow-600 transition-colors"
                      >
                        <Globe size={16} className="mr-2" />
                        {business.website}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* No Results */}
        {!isLoading && totalBusinesses === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-600 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              No businesses found matching your search criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}